
  import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import {
    Badge,
    Button,
    generateCheckClickOutside,
    Hint,
    Icon,
    IconProp,
    Select,
    Typography,
  } from '@voximplant/spaceui';
  import { useI18n } from 'vue-i18n';
  import { $signInFields, logoutFx } from '@/store/signIn/index';
  import { useStore } from 'effector-vue/composition';
  import {
    $currentComponent,
    $dialingComponentStatus,
    changeComponent,
  } from '@/store/components/index';
  import DropdownList from '@/components/common/DropdownList.vue';
  import {
    $isBannedStatus,
    $queueStatus,
    $settings,
    $softphoneParameters,
    changeQueueStatus,
    toggleMaximize,
    toggleMinimize,
  } from '@/store/settings/index';
  import { QueueType } from '@/types';
  import { OperatorACDStatuses } from 'voximplant-websdk';
  import {
    $callDuration,
    $calls,
    $currentSelectCallId,
    currentSelectCall,
    incomingCalls,
  } from '@/store/calls';
  import appConfig from '@/config';

  const __default__ = defineComponent({
    name: 'Header',
    components: { Typography, Button, Select, Badge, Hint, Icon, DropdownList },
    setup() {
      const { t } = useI18n();
      const i18n = useI18n({ useScope: 'global', inheritLocale: true });
      const settingsState = useStore($settings);
      const softphoneParameters = useStore($softphoneParameters);
      const signInStore = useStore($signInFields);
      const queueStatus = useStore($queueStatus);
      const currentComponent = useStore($currentComponent);
      const dialingComponentStatus = useStore($dialingComponentStatus);
      const selectCall = useStore(currentSelectCall);
      const selectCallId = useStore($currentSelectCallId);
      const isBannedStatus = useStore($isBannedStatus);
      const durations = useStore($callDuration);
      const incoming = useStore(incomingCalls);
      const isOpenMoreActions = ref(false);
      const tooltipVisibility = computed(() => (isOpenMoreActions.value ? 'hidden' : 'visible'));
      const toggleActionsDropdownList = (params: boolean) => {
        isOpenMoreActions.value = params;
      };
      const moreActionsBtn = ref<HTMLDivElement | null>(null);
      const checkClickOutsideBtn = generateCheckClickOutside(moreActionsBtn);
      const onClickOutside = (event: MouseEvent) => {
        if (checkClickOutsideBtn(event) && isOpenMoreActions.value) {
          toggleActionsDropdownList(!isOpenMoreActions.value);
        }
      };
      const closeSelect = (event: KeyboardEvent) => {
        const isPushKey = event.key === 'Escape';
        if (isPushKey && isOpenMoreActions.value) toggleActionsDropdownList(false);
      };
      onMounted(() => {
        document.addEventListener('click', onClickOutside, { capture: true });
        document.addEventListener('keydown', closeSelect);
      });
      onBeforeUnmount(() => {
        document.removeEventListener('click', onClickOutside);
        document.removeEventListener('keydown', closeSelect);
      });
      const moreActionsList = computed(() => [
        {
          name: t('moreActionsList.settings'),
          icon: {
            name: 'ic20-settings',
            color: '--sui-gray-700',
          },
          click: () => {
            changeComponent('Settings');
            toggleActionsDropdownList(false);
          },
        },
        {
          name: t('moreActionsList.logout'),
          click: () => {
            logoutFx({});
            toggleActionsDropdownList(false);
          },
        },
      ]);

      const calls = useStore($calls);
      const isVideoCall = computed(
        () =>
          calls.value[incoming.value[0]?.id]?.params.video ||
          calls.value[selectCallId.value]?.params.video
      );
      const isSelectCallNotEnded = computed(() => {
        return selectCall.value && selectCall.value.status !== 'ENDED';
      });

      const minimizeTitle = computed(() => {
        if (!settingsState.value.minimize) return '';

        if (currentComponent.value === 'CallEnded') return t('endedCall');
        // TITLE IF SELECTED CALL WAS ENDED
        else if (currentComponent.value === 'Dialing') {
          // TITLE IF THERE IS NO SELECTED INCOMING AND OUTBOUND CALLS
          switch (dialingComponentStatus.value) {
            case 'newCall': {
              return t('newCall');
            }
            case 'firstCall': {
              return t('newCall');
            }
            case 'toneDial': {
              return t('numpad');
            }
          }
        } else if (selectCall.value?.status === 'CONNECTED' && !selectCall.value.active)
          return t('pausedCall'); // TITLE IF SELECTED CALL'S WAS PAUSED

        if (selectCall.value?.direction === 'outbound' && isSelectCallNotEnded.value) {
          // TITLE FOR OUTBOUND AUDIO/VIDEO CALL
          return isVideoCall.value
            ? t(`videoCall.${selectCall.value?.direction}`)
            : t(`${selectCall.value?.direction}`);
        } else if (
          incoming.value.length ||
          (selectCall.value?.direction === 'incoming' && isSelectCallNotEnded.value)
        ) {
          // TITLE FOR INCOMING AUDIO/VIDEO CALL
          return isVideoCall.value ? t('videoCall.incoming') : t('incoming');
        }
      });
      const showActiveCamIcon = computed(
        () => minimizeTitle.value && isVideoCall.value && !settingsState.value.videoMute
      );

      const userName = computed<string>(() => signInStore.value.userName);
      const userID = computed<string>(
        () => `${signInStore.value.accountName}@${signInStore.value.applicationName}`
      );

      const currentStatus = computed(() => {
        const currentStatuses =
          signInStore.value.queueType === QueueType.SmartQueue ? statusesSq.value : statuses.value;
        return currentStatuses.find((item) => {
          return (
            item.value === OperatorACDStatuses[queueStatus.value] ||
            // SDK has no type SmartQueue statuses, SQ contains 'Dialing' status
            queueStatus.value === item.value.toUpperCase() // for set SQ status
          );
        });
      });

      const languages = [
        {
          label: 'English',
          value: 'en',
          icon: 'ic24-flag-usa',
        },
        {
          label: 'Русский',
          value: 'ru',
          icon: 'ic24-flag-rus',
        },
      ];

      const activeLang = ref(languages[0]);

      watch(activeLang, () => {
        i18n.locale.value = activeLang.value.value;
      });

      const statuses = computed(() => [
        {
          label: t('statuses.online'),
          value: 'Online',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-success',
          },
        },
        {
          label: t('statuses.ready'),
          value: 'Ready',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-success',
          },
        },
        {
          label: t('statuses.offline'),
          value: 'Offline',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-error',
          },
        },
        {
          label: t('statuses.dnd'),
          value: 'DND',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-error',
          },
        },
        {
          label: t('statuses.inService'),
          value: 'InService',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-processing',
          },
        },
        {
          label: t('statuses.afterService'),
          value: 'AfterService',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-default',
          },
        },
        {
          label: t('statuses.timeout'),
          value: 'Timeout',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-warning',
          },
        },
      ]);
      const statusesSq = computed(() => [
        ...statuses.value.filter((status) => status.value !== 'Timeout'),
        {
          label: t('statuses.dialing'),
          value: 'Dialing',
          icon: {
            spriteUrl: 'static/icons-pack.svg',
            name: 'ic8-status-dot-processing',
          },
        },
      ]);

      const getMinimizeIcon = computed<IconProp>(() => {
        return settingsState.value.minimize
          ? {
              spriteUrl: 'static/icons-pack.svg',
              name: 'ic20-minimize-screen-on',
              color: '--sui-gray-400',
            }
          : {
              spriteUrl: 'static/icons-pack.svg',
              name: 'ic20-minimize-screen-off',
              color: '--sui-gray-400',
            };
      });

      const getMaximizeIcon = computed<IconProp>(() => {
        return settingsState.value.maximize
          ? {
              spriteUrl: 'static/icons-pack.svg',
              name: 'ic20-maximize-screen-on',
              color: '--sui-gray-400',
            }
          : {
              spriteUrl: 'static/icons-pack.svg',
              name: 'ic20-maximize-screen-off',
              color: '--sui-gray-400',
            };
      });

      return {
        t,
        i18n,
        appConfig,
        activeLang,
        currentStatus,
        languages,
        queueStatus,
        changeQueueStatus,
        durations,
        logoutFx,
        changeComponent,
        userName,
        userID,
        toggleMaximize,
        toggleMinimize,
        statuses,
        statusesSq,
        getMinimizeIcon,
        getMaximizeIcon,
        settingsState,
        softphoneParameters,
        currentComponent,
        signInStore,
        QueueType,
        minimizeTitle,
        showActiveCamIcon,
        isOpenMoreActions,
        moreActionsBtn,
        moreActionsList,
        tooltipVisibility,
        toggleActionsDropdownList,
        isBannedStatus,
      };
    },
  });

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "2d6afc26": (_ctx.tooltipVisibility)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__